import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './VersionHistoryPanel.css'

// The op value describes how the entry was CAPTURED (a delete happened /
// a re-insert-after-delete happened) — but the label here describes what
// you GET if you click "Wiederherstellen" on it, which is the opposite of
// the capture event for delete/insert: restoring a 'delete'-tagged entry
// upserts content back in (row ends up existing → "Wiederhergestellt"),
// while restoring an 'insert'-tagged entry removes the row again (row ends
// up missing → "Gelöscht"). Deliberately swapped from what the op name
// alone would suggest — matches what the button actually does, not how the
// row got into entity_versions in the first place.
// Only correct where that button still exists, though — on timeline-restore
// tables "Wiederherstellen" was removed entirely (replaced by the
// table-wide "Gesamten Stand hierauf zurücksetzen" action), so there's no
// per-row restore outcome left for the swap to describe. There, the badge
// falls back to naming the capture event directly instead.
const OP_LABELS = { update: 'Geändert', delete: 'Wiederhergestellt', insert: 'Gelöscht' }
const OP_BADGE_CLASS = { update: 'badge-grey', delete: 'badge-green', insert: 'badge-red' }
const TIMELINE_OP_LABELS = { update: 'Geändert', delete: 'Gelöscht', insert: 'Wiederhergestellt' }
const TIMELINE_OP_BADGE_CLASS = { update: 'badge-grey', delete: 'badge-red', insert: 'badge-green' }

const fmtDateTime = (iso) => {
  const d = new Date(iso)
  return isNaN(d) ? '—' : d.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
}

// safety_rules.rules and quiz_questions.questions are both JSONB arrays of
// content blocks — dumping the raw array as JSON in the diff view is
// unreadable and, since every enabled language has its own near-identical
// array, looks the same for every language block. Show a readable per-item
// text diff instead, keyed by which field this is.
const BLOCK_ARRAY_TEXT_KEY = { rules: 'text', questions: 'question' }

// Content blocks often carry inline formatting (e.g. safety-rules headings
// as "<span style=...><b>...</b></span>") — strip tags for display, the
// diff is about the wording, not the markup.
const stripHtml = (s) => (typeof s === 'string' ? s.replace(/<[^>]+>/g, '').trim() : s)

const blockArrayItems = (textKey, arr) =>
  (Array.isArray(arr) ? arr : []).map(item => stripHtml(item?.[textKey]))

// Comparing old[i] to new[i] by raw position falls apart the moment items
// are reordered or the count changes — every item after the change looks
// "different" even though most of them are untouched, just shifted. Instead:
// match items that are exactly the same text regardless of position first
// (so reordering never shows up as a false change), then only pair up the
// genuinely leftover items — by their remaining order — to tell an edit
// apart from a plain add/remove.
const blockArrayDiffLines = (textKey, oldArr, newArr) => {
  const oldItems = (Array.isArray(oldArr) ? oldArr : []).map(item => ({ ...item, [textKey]: stripHtml(item?.[textKey]) }))
  const newItems = (Array.isArray(newArr) ? newArr : []).map(item => ({ ...item, [textKey]: stripHtml(item?.[textKey]) }))

  const usedNew = new Array(newItems.length).fill(false)
  const matchedNewIdx = new Array(oldItems.length).fill(-1)
  for (let i = 0; i < oldItems.length; i++) {
    const j = newItems.findIndex((n, idx) => !usedNew[idx] && n[textKey] === oldItems[i][textKey])
    if (j !== -1) { usedNew[j] = true; matchedNewIdx[i] = j }
  }

  const lines = []

  // Same text, same slot conceptually — only worth noting if the image changed.
  for (let i = 0; i < oldItems.length; i++) {
    const j = matchedNewIdx[i]
    if (j === -1) continue
    if (oldItems[i].image_url !== newItems[j].image_url) {
      lines.push({ type: 'image', text: oldItems[i][textKey] })
    }
  }

  // Whatever's left over (no exact text match anywhere in the other array)
  // gets paired up by remaining order — an edit shows as "changed", a pure
  // count mismatch spills over into added/removed.
  const leftoverOld = oldItems.filter((_, i) => matchedNewIdx[i] === -1)
  const leftoverNew = newItems.filter((_, idx) => !usedNew[idx])
  const max = Math.max(leftoverOld.length, leftoverNew.length)
  for (let k = 0; k < max; k++) {
    const o = leftoverOld[k]
    const n = leftoverNew[k]
    if (o && !n) lines.push({ type: 'removed', text: o[textKey] })
    else if (!o && n) lines.push({ type: 'added', text: n[textKey] })
    else lines.push({ type: 'changed', from: o[textKey], to: n[textKey] })
  }

  return lines
}

// info_pages.blocks is a different shape from rules/questions — blocks mix
// types (image vs. paragraph), and a paragraph's `text` is a whole
// {language: text} map living inside one row, not a single string scoped to
// one row/language. Each block does carry a stable `id` though, so align by
// that instead of by text — far more reliable here, and shows German (or
// whatever's first available) as the representative text per block, rather
// than dumping all ~26 languages.
const blockTextForDisplay = (block) => {
  const t = block?.text
  if (typeof t === 'string') return stripHtml(t)
  if (t && typeof t === 'object') return stripHtml(t.de || Object.values(t)[0] || '')
  return ''
}

const infoBlockLabel = (block) => {
  if (!block) return ''
  if (block.type === 'image') return block.caption ? stripHtml(block.caption) : '(Bild)'
  return blockTextForDisplay(block) || '(leer)'
}

const infoBlockItems = (arr) => (Array.isArray(arr) ? arr : []).map(infoBlockLabel)

const infoBlockTextEquals = (a, b) => {
  const ta = a?.text, tb = b?.text
  if (typeof ta === 'string' || typeof tb === 'string') return ta === tb
  return JSON.stringify(ta ?? null) === JSON.stringify(tb ?? null)
}

const infoBlockDiffLines = (oldArr, newArr) => {
  const oldItems = Array.isArray(oldArr) ? oldArr : []
  const newItems = Array.isArray(newArr) ? newArr : []
  const oldById = new Map(oldItems.filter(b => b?.id).map(b => [b.id, b]))
  const newById = new Map(newItems.filter(b => b?.id).map(b => [b.id, b]))

  const lines = []
  for (const [id, o] of oldById) {
    const n = newById.get(id)
    if (!n) { lines.push({ type: 'removed', text: infoBlockLabel(o) }); continue }
    if (o.type !== n.type || !infoBlockTextEquals(o, n)) {
      lines.push({ type: 'changed', from: infoBlockLabel(o), to: infoBlockLabel(n) })
    } else if (o.url !== n.url) {
      lines.push({ type: 'image', text: infoBlockLabel(o) })
    }
  }
  for (const [id, n] of newById) {
    if (!oldById.has(id)) lines.push({ type: 'added', text: infoBlockLabel(n) })
  }
  return lines
}

// Reusable "who changed this last, and can I revert it" panel for any table
// tracked by entity_versions (see database/migrations/019_entity_versions.sql
// and the /api/admin/versions* routes in server/routes/admin.js). Lazily
// loads its history on first expand so pages that embed it don't pay for an
// extra request until an admin actually opens it.
//
// Usage: <VersionHistoryPanel table="safety_rules" tenantId={selectedTenant} onRestored={reload} />
// Pass rowId to scope to a single row instead of every row for that tenant+table.
// Pass global for owner-only tables that have no tenant at all (rule_library,
// station_barriers) — otherwise the panel waits for a truthy tenantId before
// fetching, which a global table will never have.
// Pass title when a page renders more than one panel (e.g. License Plates
// has one for license_plates and one for license_plate_tags) — without it
// every panel just says "Verlauf" and there's no way to tell them apart.
// Pass enableTimelineRestore for tables where this panel is scoped to every
// row of a tenant (no rowId) — it replaces the default "Wiederherstellen"
// (which only ever touches the one row an entry belongs to, easily confused
// with a whole-table action once both exist side by side) with an action
// that resets the WHOLE table back to how it looked at that entry's
// timestamp (deleting rows added since, restoring rows deleted by then).
// Only offer this where the backend has verified the table for it
// (server/routes/admin.js TIMELINE_RESTORE_TABLES).
const VersionHistoryPanel = ({ table, tenantId, rowId, global = false, title = 'Verlauf', onRestored, enableTimelineRestore = false }) => {
  const { apiFetch } = useAuth()

  const [open,             setOpen]             = useState(false)
  const [loading,           setLoading]           = useState(false)
  const [groups,            setGroups]            = useState([])
  const [error,             setError]             = useState(null)
  const [restoringKey,      setRestoringKey]      = useState(null)
  const [diffs,             setDiffs]             = useState({})
  const [openDiffKey,       setOpenDiffKey]       = useState(null)
  const [timelineKey,       setTimelineKey]       = useState(null)

  const groupKey = (g) => g.saveId || g.versions[0]?.versionId

  const load = useCallback(() => {
    if (!global && !tenantId) return
    setLoading(true)
    setError(null)
    const params = new URLSearchParams({ table })
    if (tenantId) params.set('tenantId', tenantId)
    if (rowId) params.set('rowId', rowId)
    apiFetch(`/api/admin/versions?${params}`)
      .then(r => r.json())
      .then(data => setGroups(Array.isArray(data) ? data : []))
      .catch(() => setError('Verlauf konnte nicht geladen werden'))
      .finally(() => setLoading(false))
  }, [table, tenantId, rowId, global])

  useEffect(() => {
    if (open) load()
  }, [open, load])

  const restore = async (group) => {
    const count = group.versions.length
    // Same wording and button label for every entry type, including
    // 'insert' ones (where restoring means the entry stops existing again)
    // — the mental model is always "restore this version," full stop; the
    // badge (Geändert/Gelöscht/Wiederhergestellt) is what tells you which
    // state you're going back to.
    const what = count > 1 ? `Diese Version (${count} Einträge)` : 'Diese Version'
    if (!window.confirm(
      `${what} wirklich wiederherstellen? Der aktuelle Stand wird dabei ebenfalls gesichert und kann danach genauso wiederhergestellt werden.`
    )) return

    setRestoringKey(groupKey(group))
    setError(null)
    try {
      const res = count > 1
        ? await apiFetch('/api/admin/versions/restore-batch', {
            method: 'POST',
            body: JSON.stringify({ saveId: group.saveId }),
          })
        : await apiFetch(`/api/admin/versions/${group.versions[0].versionId}/restore`, { method: 'POST' })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Wiederherstellen fehlgeschlagen')

      load()
      onRestored?.()
    } catch (err) {
      setError(err.message)
    } finally {
      setRestoringKey(null)
    }
  }

  const toggleDiff = (group) => {
    const key = groupKey(group)
    if (openDiffKey === key) { setOpenDiffKey(null); return }
    setOpenDiffKey(key)
    if (diffs[key]) return
    setDiffs(d => ({ ...d, [key]: 'loading' }))
    const params = new URLSearchParams({ table, asOf: group.createdAt })
    if (tenantId) params.set('tenantId', tenantId)
    apiFetch(`/api/admin/versions/table-diff?${params}`)
      .then(r => r.json())
      .then(data => setDiffs(d => ({ ...d, [key]: data })))
      .catch(() => setDiffs(d => ({ ...d, [key]: { error: true } })))
  }

  // Per-entry field diff (used everywhere enableTimelineRestore is off) —
  // fetches one preview per version in the group (usually just one; only
  // multi-row saves like safety_rules touch more than one at a time).
  const togglePreview = (group) => {
    const key = groupKey(group)
    if (openDiffKey === key) { setOpenDiffKey(null); return }
    setOpenDiffKey(key)
    if (diffs[key]) return
    setDiffs(d => ({ ...d, [key]: 'loading' }))
    Promise.all(group.versions.map(v => apiFetch(`/api/admin/versions/${v.versionId}/preview`).then(r => r.json())))
      .then(rows => setDiffs(d => ({ ...d, [key]: rows })))
      .catch(() => setDiffs(d => ({ ...d, [key]: { error: true } })))
  }

  const fmtVal = (v) => {
    if (v === null || v === undefined || v === '') return '—'
    if (typeof v === 'object') return JSON.stringify(v)
    return String(v)
  }

  // mode: 'current' (deletion preview, only f.current exists), 'restored'
  // (row-doesn't-exist-yet preview, only f.restored exists), or 'diff'
  // (both sides, ordinary change). rules/questions/blocks get readable
  // per-item block-array rendering; every other field keeps the plain
  // "key: value" / "key: old → new" line.
  const renderFieldLine = (f, mode) => {
    const isInfoBlocks = f.key === 'blocks'
    const textKey = BLOCK_ARRAY_TEXT_KEY[f.key]
    if (!isInfoBlocks && !textKey) {
      return mode === 'diff'
        ? <li key={f.key}>{f.key}: {fmtVal(f.current)} → {fmtVal(f.restored)}</li>
        : <li key={f.key}>{f.key}: {fmtVal(mode === 'current' ? f.current : f.restored)}</li>
    }

    if (mode !== 'diff') {
      const arr = mode === 'current' ? f.current : f.restored
      const items = isInfoBlocks ? infoBlockItems(arr) : blockArrayItems(textKey, arr)
      return (
        <li key={f.key} className="vhp-diff-block-field">
          <span className="vhp-diff-block-label">{f.key}:</span>
          <ul className="vhp-diff-fields">
            {items.map((text, i) => <li key={i}>{text || '—'}</li>)}
          </ul>
        </li>
      )
    }

    const lines = isInfoBlocks
      ? infoBlockDiffLines(f.current, f.restored)
      : blockArrayDiffLines(textKey, f.current, f.restored)
    return (
      <li key={f.key} className="vhp-diff-block-field">
        <span className="vhp-diff-block-label">{f.key}:</span>
        {lines.length === 0 ? (
          <div className="vhp-diff-block-note">Reihenfolge oder weitere Details geändert.</div>
        ) : (
          <ul className="vhp-diff-fields">
            {lines.map((l, i) => (
              <li key={i}>
                {l.type === 'removed' && <>− {l.text || '—'}</>}
                {l.type === 'added'   && <>+ {l.text || '—'}</>}
                {l.type === 'changed' && <>{l.from || '—'} → {l.to || '—'}</>}
                {l.type === 'image'   && <>{l.text || '—'} (Bild geändert)</>}
              </li>
            ))}
          </ul>
        )}
      </li>
    )
  }

  const diffSummaryLine = (diff) => {
    const parts = []
    if (diff.toDelete.length)  parts.push(`${diff.toDelete.length} gelöscht`)
    if (diff.toRestore.length) parts.push(`${diff.toRestore.length} wiederhergestellt`)
    if (diff.toUpdate.length)  parts.push(`${diff.toUpdate.length} geändert`)
    return parts.length ? parts.join(', ') : 'keine Unterschiede zum aktuellen Stand'
  }

  const restoreToTimestamp = async (group) => {
    const key = groupKey(group)
    let diff = diffs[key]
    if (!diff || diff === 'loading' || diff.error) {
      const params = new URLSearchParams({ table, asOf: group.createdAt })
      if (tenantId) params.set('tenantId', tenantId)
      try {
        diff = await apiFetch(`/api/admin/versions/table-diff?${params}`).then(r => r.json())
        setDiffs(d => ({ ...d, [key]: diff }))
      } catch {
        diff = null
      }
    }

    const summary = diff ? diffSummaryLine(diff) : 'Vorschau nicht verfügbar'
    if (!window.confirm(
      `Gesamten Stand auf diesen Zeitpunkt zurücksetzen (${summary})?\n\n` +
      'Dies betrifft ALLE Einträge, nicht nur diesen einen. Der aktuelle Stand jeder betroffenen Zeile wird dabei ebenfalls gesichert und kann danach wiederhergestellt werden.'
    )) return

    setTimelineKey(key)
    setError(null)
    try {
      const res = await apiFetch('/api/admin/versions/restore-table-as-of', {
        method: 'POST',
        body: JSON.stringify({ table, tenantId, asOf: group.createdAt }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Zurücksetzen fehlgeschlagen')
      setDiffs({})
      setOpenDiffKey(null)
      load()
      onRestored?.()
    } catch (err) {
      setError(err.message)
    } finally {
      setTimelineKey(null)
    }
  }

  return (
    <div className="card vhp-card">
      <button type="button" className="vhp-toggle" onClick={() => setOpen(o => !o)}>
        <span className="section-title vhp-title">{title}</span>
        <span className={`vhp-chevron${open ? ' vhp-chevron--open' : ''}`}>▾</span>
      </button>

      {open && (
        <div className="vhp-body">
          {error && <div className="alert alert-error">{error}</div>}

          {loading ? (
            <div className="loading-state"><span className="spinner" /></div>
          ) : groups.length === 0 ? (
            <div className="empty-state vhp-empty">
              <p>Noch keine Änderungen protokolliert.</p>
            </div>
          ) : (
            <ul className="vhp-list">
              {groups.map(group => {
                const key   = groupKey(group)
                const count = group.versions.length
                const diff = diffs[key]
                return (
                  <li key={key} className="vhp-item">
                    <div className="vhp-item-row">
                      <div className="vhp-item-info">
                        {group.contextLabel && <span className="vhp-item-context">{group.contextLabel}</span>}
                        <span className="vhp-item-date">{fmtDateTime(group.createdAt)}</span>
                        <span className="vhp-item-by">{group.changedBy || 'Unbekannt'}</span>
                        <span className={`badge ${(enableTimelineRestore ? TIMELINE_OP_BADGE_CLASS : OP_BADGE_CLASS)[group.op] || 'badge-grey'} vhp-badge`}>
                          {(enableTimelineRestore ? TIMELINE_OP_LABELS : OP_LABELS)[group.op] || group.op}
                          {count > 1 ? ` · ${count} Einträge` : ''}
                        </span>
                        {group.isCurrent && (
                          <span
                            className="badge badge-blue vhp-badge"
                            title={enableTimelineRestore
                              ? 'Entspricht dem aktuellen Stand — Zurücksetzen würde nichts ändern'
                              : 'Entspricht dem aktuellen Stand — Wiederherstellen würde nichts ändern'}
                          >
                            Aktueller Stand
                          </span>
                        )}
                      </div>
                      <div className="vhp-item-actions">
                        <button
                          type="button"
                          className="vhp-info-btn"
                          onClick={() => (enableTimelineRestore ? toggleDiff(group) : togglePreview(group))}
                          title={enableTimelineRestore
                            ? 'Was würde sich beim Zurücksetzen auf diesen Zeitpunkt ändern?'
                            : 'Was würde sich beim Wiederherstellen ändern?'}
                        >
                          ⓘ
                        </button>
                        {enableTimelineRestore ? (
                          <button
                            className="btn btn-ghost vhp-restore-btn"
                            onClick={() => restoreToTimestamp(group)}
                            disabled={timelineKey === key || group.isCurrent}
                          >
                            {timelineKey === key
                              ? <><span className="spinner vhp-spinner-sm" /> Wird zurückgesetzt…</>
                              : group.isCurrent ? 'Bereits aktueller Stand' : 'Gesamten Stand hierauf zurücksetzen'}
                          </button>
                        ) : (
                          <button
                            className="btn btn-ghost vhp-restore-btn"
                            onClick={() => restore(group)}
                            disabled={restoringKey === key || group.isCurrent}
                          >
                            {restoringKey === key
                              ? <><span className="spinner vhp-spinner-sm" /> Wird wiederhergestellt…</>
                              : group.isCurrent ? 'Bereits aktueller Stand' : 'Wiederherstellen'}
                          </button>
                        )}
                      </div>
                    </div>

                    {openDiffKey === key && (
                      <div className="vhp-diff-box">
                        {diff === 'loading' ? (
                          <span className="spinner spinner-sm" />
                        ) : !diff || diff.error ? (
                          <span>Vorschau konnte nicht geladen werden</span>
                        ) : enableTimelineRestore ? (
                          diff.toDelete.length === 0 && diff.toRestore.length === 0 && diff.toUpdate.length === 0 ? (
                            <span>Keine Unterschiede zum aktuellen Stand.</span>
                          ) : (
                            <>
                              {diff.toDelete.length > 0 && (
                                <div>Würde gelöscht werden: {diff.toDelete.map(r => r.label).join(', ')}</div>
                              )}
                              {diff.toRestore.length > 0 && (
                                <div>Würde wiederhergestellt werden: {diff.toRestore.map(r => r.label).join(', ')}</div>
                              )}
                              {diff.toUpdate.length > 0 && (
                                <div>Würde geändert werden: {diff.toUpdate.map(r => r.label).join(', ')}</div>
                              )}
                            </>
                          )
                        ) : (
                          diff.map((p, i) => (
                            <div key={i} className="vhp-diff-row">
                              <div className="vhp-diff-row-label">{p.label}</div>
                              {!p.existsNow && !p.willExistAfterRestore ? (
                                <div>Kein Unterschied zum aktuellen Stand — die Zeile ist bereits gelöscht.</div>
                              ) : !p.willExistAfterRestore ? (
                                <>
                                  <div>Zeile würde gelöscht werden:</div>
                                  <ul className="vhp-diff-fields">
                                    {p.fields.map(f => renderFieldLine(f, 'current'))}
                                  </ul>
                                </>
                              ) : !p.existsNow ? (
                                <>
                                  <div>Zeile würde wiederhergestellt werden mit:</div>
                                  <ul className="vhp-diff-fields">
                                    {p.fields.map(f => renderFieldLine(f, 'restored'))}
                                  </ul>
                                </>
                              ) : p.fields.length > 0 ? (
                                <ul className="vhp-diff-fields">
                                  {p.fields.map(f => renderFieldLine(f, 'diff'))}
                                </ul>
                              ) : (
                                <div>Keine Unterschiede.</div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default VersionHistoryPanel
